import { isEmpty } from '@nirvana/shared/basic-types/object';
import { html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../../utils/translator.helper';
import { BehaviorProfileListItem, BehaviorProfilesOutput, BehaviorProfileStatus } from '../../types';
import { PhasrRestrictedProfilesListWebComponent } from '../../phasrRestrictedProfilesList';
import { PhasrEvents } from '../../../../types/common/phasrTypes';

/**
 * @summary This component is responsible for displaying the list of departments, users and profiles. It also handles the selection state of each item in the list.
 * @tag gz-phasr-restricted-profiles-view
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-phasr-restricted-profiles-view')
export class PhasrRestrictedProfilesViewWebComponent extends LitElement {
  private _selectedProfilesCount = 0;

  /**
   * The total number of selected profiles
   */
  @property({ type: String, attribute: true, reflect: true })
  get selectedProfilesCount(): number {
    return this._selectedProfilesCount;
  }

  @property({ type: Array, attribute: true, reflect: false })
  departments: BehaviorProfileListItem[] = [];

  @property({ type: Array, attribute: true, reflect: false })
  users: BehaviorProfileListItem[] = [];

  @property({ type: Array, attribute: true, reflect: false })
  profiles: BehaviorProfileListItem[] = [];

  @property({ type: Boolean, attribute: true, reflect: true })
  searchMode = false;

  /**
   * The disabled state of the component. If true, all the checkboxes in the lists will be disabled.
   */
  @property({ type: Boolean })
  disabled = false;

  @query('#departments')
  departmentsList: PhasrRestrictedProfilesListWebComponent;

  @query('#users')
  usersList: PhasrRestrictedProfilesListWebComponent;

  @query('#profiles')
  profilesList: PhasrRestrictedProfilesListWebComponent;

  loadMoreUsers?: (id: string) => Promise<BehaviorProfileListItem[]>;

  loadMoreDevices?: (id: string) => Promise<BehaviorProfileListItem[]>;

  @state()
  public selectedOutput: BehaviorProfilesOutput = {
    departments: [],
    users: [],
    profiles: [],
  };

  /**
   * The internal state of the component. This is used to keep track of the selection state of each item in the list.
   * @private
   */
  @property({ type: Object })
  private internalState: Record<
    string,
    {
      checked: boolean;
      indeterminate: boolean;
      totalProfiles: number;
      totalRestrictedProfiles: number;
      users?: Record<
        string,
        {
          checked: boolean;
          indeterminate: boolean;
          totalProfiles: number;
          totalRestrictedProfiles: number;
          profiles?: Record<
            string,
            {
              checked: boolean;
            }
          >;
        }
      >;
    }
  > = {};

  set selectedProfilesCount(value: number) {
    this._selectedProfilesCount = value;
    this.dispatchEvent(
      new CustomEvent(PhasrEvents.SELECTED_PROFILES_CHANGED, {
        detail: { count: value },
        bubbles: true,
        composed: true,
      })
    );
  }

  public resetInternalState(): void {
    this.internalState = {};
  }

  protected willUpdate(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('users') && this.users.length > 0) {
      this.setUsersInInternalState(this.users);
    }
    if (changedProperties.has('departments') && this.departments.length > 0) {
      this.setDepartmentsInInternalState(this.departments);
    }
    if (changedProperties.has('profiles') && this.profiles.length > 0) {
      this.setProfilesInInternalState(this.profiles);
    }
  }

  /**
   * Updates the internal state using the provided list of department behavior profiles.
   * This method iterates over an array of department items and merges each into the current internal state.
   * For each department not already present in the state, it creates a new entry with the department's ID and status.
   * @param departments - An array of BehaviorProfileListItem representing departments to be integrated into the internal state.
   */
  private setDepartmentsInInternalState(departments: BehaviorProfileListItem[]): void {
    this.internalState = departments.reduce(
      (currentState, dept) => {
        if (!currentState[dept.id]) {
          currentState[dept.id] = {
            checked: dept.status === BehaviorProfileStatus.SELECTED,
            indeterminate: dept.status === BehaviorProfileStatus.PARTIAL,
            totalProfiles: dept.totalProfiles as number,
            totalRestrictedProfiles:
              dept.status === BehaviorProfileStatus.SELECTED
                ? (dept.totalProfiles as number)
                : (dept.totalRestrictedProfiles as number),
            users: {},
          };
        }
        return currentState;
      },
      { ...this.internalState } as Record<
        string,
        {
          checked: boolean;
          indeterminate: boolean;
          totalProfiles: number;
          totalRestrictedProfiles: number;
          users?: Record<
            string,
            {
              checked: boolean;
              indeterminate: boolean;
              totalProfiles: number;
              totalRestrictedProfiles: number;
              profiles?: Record<string, { checked: boolean }>;
            }
          >;
        }
      >
    );
  }

  /**
   * Updates the component's internal state by grouping and merging user profiles according to their department.
   * @param users - The array of BehaviorProfileListItem objects to process and integrate into the internal state.
   *
   * @remarks
   * This function ensures that the internal state maintains a consistent grouping by department and keeps track
   * of selection states (checked/indeterminate) both on a department and user level.
   */
  private setUsersInInternalState(users: BehaviorProfileListItem[]): void {
    // Group users by their department (parentId)
    const groupedUsers: Record<string, BehaviorProfileListItem[]> = users.reduce((acc, user) => {
      const deptId = user.parentId || '';
      if (!acc[deptId]) {
        acc[deptId] = [];
      }
      acc[deptId].push(user);
      return acc;
    }, {} as Record<string, BehaviorProfileListItem[]>);

    // Merge new users with any existing ones in the internalState
    Object.entries(groupedUsers).forEach(([deptId, deptUsers]) => {
      // Ensure the department exists and the users object is defined.
      if (!this.internalState[deptId]) {
        // Optionally, initialize the department if needed
        this.internalState[deptId] = {
          checked: false,
          indeterminate: false,
          totalProfiles: 0,
          totalRestrictedProfiles: 0,
          users: {},
        };
      }
      if (!this.internalState[deptId].users) {
        this.internalState[deptId].users = {};
      }
      deptUsers.forEach(user => {
        // If the user is not already added, then append it; otherwise, keep the existing state.
        if (!this.internalState[deptId].users?.[user.id]) {
          let checked = false;
          let indeterminate = false;
          let totalRestrictedProfiles = 0;

          // Use department state as context. If department is fully checked then mark user as checked.
          if (this.internalState[deptId].checked) {
            checked = true;
            totalRestrictedProfiles = (user.totalProfiles as number) ?? 0;
          } else if (this.internalState[deptId].indeterminate) {
            // Optionally, update based on user's own status:
            switch (user.status) {
              case BehaviorProfileStatus.SELECTED:
                checked = true;
                totalRestrictedProfiles = (user.totalProfiles as number) ?? 0;
                break;
              case BehaviorProfileStatus.PARTIAL:
                indeterminate = true;
                break;
              default:
                break;
            }
          }

          // @ts-ignore
          this.internalState[deptId].users[user.id] = {
            checked,
            indeterminate,
            totalProfiles: (user.totalProfiles as number) ?? 0,
            totalRestrictedProfiles,
            profiles: {},
          };
        }
      });
    });
  }

  /**
   * Updates user profiles in the internal state based on the profile list.
   *
   * Iterates over each profile, determines the corresponding department and user in the internal state,
   * and sets the profile's "checked" status using the user's state and profile status.
   *
   * @param profiles - Array of behavior profile items to update.
   */
  private setProfilesInInternalState(profiles: BehaviorProfileListItem[]): void {
    profiles.forEach(profile => {
      const userId = profile.parentId;
      if (!userId) return;
      const departmentId = Object.keys(this.internalState).find(
        deptId => this.internalState[deptId].users && this.internalState[deptId].users?.[userId]
      );
      if (!departmentId) return;

      const userState = this.internalState[departmentId]?.users?.[userId];
      if (!userState) return;

      if (!userState.profiles) {
        userState.profiles = {};
      }

      // Check if profile already exists in internal state before setting its value
      // If profile already exists, we keep its current state
      if (!userState.profiles[profile.id]) {
        let profileChecked: boolean;
        if (userState.checked) {
          profileChecked = true;
        } else if (!userState.checked && !userState.indeterminate) {
          profileChecked = false;
        } else {
          profileChecked = profile.status === BehaviorProfileStatus.SELECTED;
        }

        userState.profiles[profile.id] = { checked: profileChecked };
      }
    });
  }

  /**
   * This method will be called when a checkbox is changed in the list
   * @param apiType
   * @param id
   * @param status
   * @private
   */
  private updateSelection(apiType: keyof BehaviorProfilesOutput, id: string, status: BehaviorProfileStatus): void {
    this.selectedOutput[apiType] = this.selectedOutput[apiType].filter(entry => Object.keys(entry)[0] !== id);

    // Add new entry with updated selection state
    this.selectedOutput[apiType].push({ [id]: status });

    // Cleanup logic
    if (apiType === 'users') {
      // If a user is selected/deselected, remove any profiles associated with that user
      this.selectedOutput.profiles = this.selectedOutput.profiles.filter(profile => {
        const profileId = Object.keys(profile)[0];
        const profileParentId = this.profiles.find(p => p.id === profileId)?.parentId;
        return profileParentId !== id;
      });
    } else if (apiType === 'departments') {
      // If a department is selected/deselected, remove any profiles and users associated with that department
      this.selectedOutput.users = this.selectedOutput.users.filter(user => {
        const userId = Object.keys(user)[0];
        const userParentId = this.users.find(u => u.id === userId)?.parentId;
        return userParentId !== id;
      });
      this.selectedOutput.profiles = this.selectedOutput.profiles.filter(profile => {
        const profileId = Object.keys(profile)[0];
        const profileParentId = this.profiles.find(p => p.id === profileId)?.parentId;
        const userParentId = this.users.find(u => u.id === profileParentId)?.parentId;
        return userParentId !== id;
      });
    }
  }

  private onItemCheckboxChange(e: CustomEvent): void {
    const status = e.detail.checked ? BehaviorProfileStatus.SELECTED : BehaviorProfileStatus.UNSELECTED;
    const { apiType, value, parentId, checked, departmentId } = e.detail;

    this.updateSelection(apiType, value, status);

    if (apiType === 'departments') {
      this.onDepartmentCheckboxChange(value, checked);
    } else if (apiType === 'users') {
      this.onUserCheckboxChange(value, parentId, checked);
    } else if (apiType === 'profiles') {
      this.onProfileCheckboxChange(value, parentId, departmentId, checked);
    }

    this.dispatchEvent(
      new CustomEvent(`${e.detail.apiType}-change`, {
        detail: {
          ...e.detail,
          output: this.selectedOutput,
        },
      })
    );
  }

  /**
   * Updates the internal state for a specific department and its nested users and profiles based on the department's checkbox value,
   * and triggers a UI update along with recalculating the total restricted profiles.
   *
   * @param departmentId - The unique identifier of the department.
   * @param checked - The new checkbox state; true if checked, false otherwise.
   */
  private onDepartmentCheckboxChange(departmentId: string, checked: boolean): void {
    // Update internalState for the department if it exists
    if (this.internalState[departmentId]) {
      this.internalState[departmentId].checked = checked;
      this.internalState[departmentId].indeterminate = false;
      this.internalState[departmentId].totalRestrictedProfiles = checked
        ? this.internalState[departmentId].totalProfiles
        : 0;

      const departmentUsers = this.internalState[departmentId]?.users;

      // If users exist under this department, update each user's state
      if (!isEmpty(departmentUsers)) {
        Object.values(departmentUsers || {}).forEach(userState => {
          userState.checked = checked;
          userState.indeterminate = false;
          userState.totalRestrictedProfiles = checked ? userState.totalProfiles : 0;
          // If profiles exist under the user, update each profile state to match the user's
          if (userState.profiles) {
            Object.values(userState.profiles).forEach(profileState => {
              profileState.checked = checked;
            });
          }
        });
      }
    }

    this.requestUpdate();
    this.updateTotalRestrictedProfiles();
  }

  /**
   * Handles the change event of a user's checkbox.
   *
   * Updates the user's selection state and the corresponding profiles, adjusts the user's
   * and department's restricted profiles counts, and sets the department's status (checked
   * and indeterminate) based on the updated values.
   *
   * @param userId - Unique identifier of the user.
   * @param departmentId - Unique identifier of the department.
   * @param checked - The new checked state of the user's checkbox.
   */
  private onUserCheckboxChange(userId: string, departmentId: string, checked: boolean): void {
    if (
      !this.internalState[departmentId] ||
      !this.internalState[departmentId].users ||
      !this.internalState[departmentId]?.users?.[userId]
    )
      return;

    const userInternal = this.internalState[departmentId].users?.[userId];
    if (!userInternal) {
      return;
    }
    const departmentInternal = this.internalState[departmentId];

    // Store the previous restricted count for proper decrement
    const previousUserRestricted = userInternal.totalRestrictedProfiles || 0;

    if (checked) {
      userInternal.checked = true;
      userInternal.indeterminate = false;
      userInternal.totalRestrictedProfiles = userInternal.totalProfiles;
      if (userInternal.profiles) {
        Object.values(userInternal.profiles).forEach(profile => (profile.checked = true));
      }
      // Increment department's restricted count based on the new user's value
      departmentInternal.totalRestrictedProfiles += userInternal.totalRestrictedProfiles - previousUserRestricted;
    } else {
      userInternal.checked = false;
      userInternal.indeterminate = false;
      userInternal.totalRestrictedProfiles = 0;
      if (userInternal.profiles) {
        Object.values(userInternal.profiles).forEach(profile => (profile.checked = false));
      }
      // Decrement the department's restricted count based on the user's previous value
      departmentInternal.totalRestrictedProfiles -= previousUserRestricted;
    }

    // Update department status based on its total restricted profiles
    if (departmentInternal.totalRestrictedProfiles === 0) {
      departmentInternal.checked = false;
      departmentInternal.indeterminate = false;
    } else if (departmentInternal.totalRestrictedProfiles === departmentInternal.totalProfiles) {
      departmentInternal.checked = true;
      departmentInternal.indeterminate = false;
    } else {
      departmentInternal.checked = false;
      departmentInternal.indeterminate = true;
    }

    this.requestUpdate();
    this.updateTotalRestrictedProfiles();
  }

  /**
   * Handles profile checkbox changes.
   * Updates the internal state for the profile, user, and department, then recalculates the restricted profiles count.
   *
   * @param profileId Unique profile id.
   * @param userId Unique user id.
   * @param departmentId Department id; if missing, it is derived.
   * @param checked New checkbox state.
   */
  private onProfileCheckboxChange(profileId: string, userId: string, departmentId: string, checked: boolean): void {
    // If departmentId is missing, try to derive it from internalState using the userId.
    if (!departmentId) {
      departmentId = Object.keys(this.internalState).find(deptId => this.internalState[deptId].users?.[userId]) || '';
      if (!departmentId) {
        return;
      }
    }

    const deptState = this.internalState[departmentId];
    if (!deptState || !deptState.users || !deptState.users[userId]) {
      return;
    }

    const userState = deptState.users[userId];
    if (!userState.profiles) {
      userState.profiles = {};
    }

    // Calculate the selection delta by comparing the profile's previous state with its new 'checked' value, then update the profile's state accordingly.
    const previousChecked = userState.profiles[profileId]?.checked || false;
    userState.profiles[profileId] = { checked };
    if (!previousChecked && checked) {
      userState.totalRestrictedProfiles = (userState.totalRestrictedProfiles || 0) + 1;
    } else if (previousChecked && !checked) {
      userState.totalRestrictedProfiles = (userState.totalRestrictedProfiles || 0) - 1;
    }

    // Update user's checked and indeterminate status based on totalProfiles
    if (userState.totalRestrictedProfiles === 0) {
      userState.checked = false;
      userState.indeterminate = false;
    } else if (userState.totalRestrictedProfiles === userState.totalProfiles) {
      userState.checked = true;
      userState.indeterminate = false;
    } else {
      userState.checked = false;
      userState.indeterminate = true;
    }

    // Update department's total restricted profiles based on the change in the current user's profile selection
    const delta = !previousChecked && checked ? 1 : previousChecked && !checked ? -1 : 0;
    deptState.totalRestrictedProfiles += delta;

    // Update department's checked and indeterminate status based on totalProfiles
    if (deptState.totalRestrictedProfiles === 0) {
      deptState.checked = false;
      deptState.indeterminate = false;
    } else if (deptState.totalRestrictedProfiles === deptState.totalProfiles) {
      deptState.checked = true;
      deptState.indeterminate = false;
    } else {
      deptState.checked = false;
      deptState.indeterminate = true;
    }
    this.requestUpdate();
    this.updateTotalRestrictedProfiles();
  }

  private onItemSelect(e: CustomEvent): void {
    if (this.searchMode && e.detail.apiType === 'departments') {
      return;
    }
    // check if e.detail.id exists in the checkedState object and get the selection status for the item
    const existingEntry = this.internalState[e.detail.apiType]?.[e.detail.id];
    const selectionStatus = existingEntry ? existingEntry[e.detail.id] : undefined;

    // the selection status will be used to determine the new status of the loaded items so we can correctly update the checkboxes UI
    this.dispatchEvent(
      new CustomEvent(`${e.detail.apiType}-select`, {
        detail: {
          ...e.detail,
          selectionStatus,
        },
      })
    );
  }

  static get styles() {
    return styles;
  }

  /**
   * This method will be called when the departments list is rendered, and it will update the selection state of each checkbox in the list.
   * @private
   */
  private getDepartmentsUpdatedStates(): BehaviorProfileListItem[] {
    return this.departments.map(department => {
      const internalDeptState = this.internalState[department.id];
      if (internalDeptState) {
        return {
          ...department,
          status: internalDeptState.checked
            ? BehaviorProfileStatus.SELECTED
            : internalDeptState.indeterminate
            ? BehaviorProfileStatus.PARTIAL
            : BehaviorProfileStatus.UNSELECTED,
          totalRestrictedProfiles: internalDeptState.totalRestrictedProfiles,
        };
      }
      return department;
    });
  }

  /**
   * This method will be called when the users list is rendered, and it will update the selection state of each checkbox in the list.
   * This transformation is necessary because the users list is loaded after the departments list, so we need to update the selection state of each user based on the selection state of the departments.
   * @private
   */
  private getUsersUpdatedStates(): BehaviorProfileListItem[] {
    return this.users.map(user => {
      if (user.parentId && this.internalState[user.parentId]?.users?.[user.id]) {
        const userState = this.internalState[user.parentId]?.users?.[user.id];
        return {
          ...user,
          status: userState?.checked
            ? BehaviorProfileStatus.SELECTED
            : userState?.indeterminate
            ? BehaviorProfileStatus.PARTIAL
            : BehaviorProfileStatus.UNSELECTED,
          totalRestrictedProfiles: userState?.totalRestrictedProfiles,
        };
      }
      return user;
    });
  }

  /**
   * This method will be called when the profiles list is rendered, and it will update the selection state of each checkbox in the list.
   * This transformation is necessary because the profiles list is loaded after the users and deparments list, so we need to update the selection state of each profile based on the selection state of these lists.
   * @private
   */
  private getProfilesUpdatedStates(): BehaviorProfileListItem[] {
    return this.profiles.map(profile => {
      if (profile.parentId) {
        const departmentId = Object.keys(this.internalState).find(
          deptId =>
            this.internalState[deptId].users &&
            profile.parentId &&
            this.internalState[deptId]?.users?.[profile.parentId]
        );
        if (departmentId) {
          const userState = this.internalState[departmentId].users?.[profile.parentId];
          if (userState?.profiles && profile.id in userState.profiles) {
            return {
              ...profile,
              status: userState.profiles[profile.id].checked
                ? BehaviorProfileStatus.SELECTED
                : BehaviorProfileStatus.UNSELECTED,
            };
          }
        }
      }
      return profile;
    });
  }

  /**
   * Computes the total number of restricted profiles across all departments
   * @private
   */
  private updateTotalRestrictedProfiles(): void {
    this.selectedProfilesCount = Object.values(this.internalState).reduce(
      (sum, dept) => sum + dept.totalRestrictedProfiles,
      0
    );
  }

  private renderSummary(): string {
    if (this.selectedProfilesCount > 0) {
      return translateFn('You have {OPEN_TAG} {numTotalItems} {CLOSE_TAG} profiles selected.', {
        numTotalItems: this.selectedProfilesCount,
        OPEN_TAG: '<span class="emphasized">',
        CLOSE_TAG: '</span>',
      });
    }

    return translateFn('No profiles selected.');
  }

  render() {
    return html`
        <div class="content">
            <gz-phasr-restricted-profiles-list
              ?disabled="${this.disabled}"
              id="departments"
              apiType="departments"
              class="column"
              @select="${this.onItemSelect}"
              @change="${this.onItemCheckboxChange}"
              .listTitle="${translateFn('Departments')}"
              .items="${this.getDepartmentsUpdatedStates()}"
            ></gz-phasr-restricted-profiles-list>
            <gz-phasr-restricted-profiles-list
              ?disabled="${this.disabled}"
              id="users"
              apiType="users"
              class="column"
              @select="${this.onItemSelect}"
              @change="${this.onItemCheckboxChange}"
              .listTitle="${translateFn('Users')}"
              .items="${this.getUsersUpdatedStates()}"
              .loadMoreItem="${this.loadMoreUsers}"
              />
            </gz-phasr-restricted-profiles-list>
            <gz-phasr-restricted-profiles-list
              ?disabled="${this.disabled}"
              id="profiles"
              apiType="profiles"
              class="column"
              @select="${(e: CustomEvent) =>
                this.dispatchEvent(new CustomEvent('profile-select', { detail: e.detail }))}"
              @change="${this.onItemCheckboxChange}"
              .listTitle="${translateFn('Devices')}"
              .items="${this.getProfilesUpdatedStates()}"
              .loadMoreItem="${this.loadMoreDevices}"
            ></gz-phasr-restricted-profiles-list>
          <div class="column" id="summary">
            <gz-title class="list-title" fontWeight="600" fontSize="11">${translateFn(
              'Selected Behavioral profiles'
            ).toUpperCase()}</gz-title>
            <gz-text fontSize="13" class="selection-summary" .innerHTML="${this.renderSummary()}">
            </gz-text fontSize="13">
          </div>
        </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-phasr-restricted-profiles-view': PhasrRestrictedProfilesViewWebComponent;
  }
}
